/**
 * What the panels of a repeated tile share, on screen and in PowerPoint
 * alike: one value-axis end, one legend, and the grid they fill. The
 * series here are the ones the chart tiles draw (see BarTile, LineTile,
 * PieTile and the export's charts), read from each panel's view.
 */

import type { TableModel } from "utils/tableModel"
import {
  modelToBarSeries,
  modelToLineSeries,
  modelToPieSlices,
  shapeBarSeries,
  sortSlices,
} from "features/Dashboard/runtime/modelAdapters"
import type { RepeatTile } from "features/Dashboard/runtime/panels"
import {
  entityColours,
  hiddenColumns,
  stackEnds,
  tileEntities,
} from "./chartTheme"
import type { LegendItem } from "./chartTheme"

const NICE_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]

/** `v` rounded up to a round number: 0.4375 to 0.5, 1234 to 1500. */
export const niceCeil = (v: number): number => {
  if (!Number.isFinite(v) || v <= 0) return v
  const magnitude = 10 ** Math.floor(Math.log10(v))
  const step =
    NICE_STEPS.find((s) => v / magnitude <= s * (1 + 1e-9)) ?? 10
  return Number((step * magnitude).toPrecision(12))
}

/** The values a bar or line panel plots: stack totals for stacked bars. */
const plottedValues = (
  tile: RepeatTile,
  view: TableModel,
  statistic: string
): (number | null)[] => {
  if (tile.type === "line") {
    return modelToLineSeries(view, {
      statistic,
      hiddenSeries: tile.visual.hiddenSeries,
      waveVariable: tile.visual.waveVariable,
    }).series.flatMap((s) => s.values)
  }
  if (tile.type !== "bar") return []
  const v = tile.visual
  const s = shapeBarSeries(
    modelToBarSeries(view, {
      statistic,
      seriesAxis: v.seriesAxis,
      showTotals: v.showTotals,
      hiddenCategories: hiddenColumns(v),
    }),
    { mode: v.mode, normalize: tile.chart.normalize, sort: tile.chart.sort }
  )
  return v.mode === "stacked"
    ? stackEnds(
        s.series.map((x) => x.values),
        s.categories.length
      )
    : s.series.flatMap((x) => x.values)
}

/**
 * The value-axis end every panel of a bar or line tile shares, in plotted
 * units: the largest value any panel plots, rounded up. Undefined for a
 * pie, when the tile fixes its own axis end, and when no panel plots a
 * positive value (each panel then finds its own end).
 */
export const sharedAxisTop = (
  tile: RepeatTile,
  views: TableModel[],
  statistic: string
): number | undefined => {
  if (tile.type === "pie" || tile.chart.axisMax !== undefined) return undefined
  const top = views.reduce((m, view) => {
    const values = plottedValues(tile, view, statistic).filter(
      (x): x is number => x !== null && Number.isFinite(x)
    )
    return values.reduce((a, x) => Math.max(a, x), m)
  }, 0)
  return top > 0 ? niceCeil(top) : undefined
}

/**
 * The keys of the one legend a repeated tile shows under its panels, read
 * from a panel's view: the series of a bar or line tile, the slices of a
 * pie, each in the colour the panel charts give it (worked out from `raw`,
 * a panel's last result, see `entityColours`).
 */
export const panelLegendItems = (
  tile: RepeatTile,
  view: TableModel,
  raw: TableModel,
  statistic: string,
  paletteId: string | undefined,
  colourMap: Record<string, string>
): LegendItem[] => {
  const entities = tileEntities(tile)
  if (tile.type === "pie") {
    const colourOf = entityColours(raw, "down", paletteId, colourMap, entities)
    const pie = modelToPieSlices(view, { statistic })
    return sortSlices(pie.slices, tile.chart.sort).map((s) => ({
      id: s.id,
      label: s.label,
      color: colourOf(s.id),
    }))
  }
  const series =
    tile.type === "line"
      ? modelToLineSeries(view, {
          statistic,
          hiddenSeries: tile.visual.hiddenSeries,
          waveVariable: tile.visual.waveVariable,
        }).series
      : modelToBarSeries(view, {
          statistic,
          seriesAxis: tile.visual.seriesAxis,
          showTotals: tile.visual.showTotals,
          hiddenCategories: hiddenColumns(tile.visual),
        }).series
  const colourOf = entityColours(
    raw,
    tile.type === "line" ? "down" : tile.visual.seriesAxis,
    paletteId,
    colourMap,
    entities
  )
  return series.map((s) => ({ id: s.id, label: s.label, color: colourOf(s.id) }))
}

/**
 * The rows the panels of `tile` leave out for being in another unit than
 * their first, read from a panel's view (every panel has the same rows).
 */
export const panelOmitted = (
  tile: RepeatTile,
  view: TableModel,
  statistic: string
): string[] => {
  switch (tile.type) {
    case "pie":
      return modelToPieSlices(view, { statistic }).omitted
    case "line":
      return modelToLineSeries(view, {
        statistic,
        hiddenSeries: tile.visual.hiddenSeries,
        waveVariable: tile.visual.waveVariable,
      }).omitted
    case "bar":
      return modelToBarSeries(view, {
        statistic,
        seriesAxis: tile.visual.seriesAxis,
        showTotals: tile.visual.showTotals,
        hiddenCategories: hiddenColumns(tile.visual),
      }).omitted
  }
}

/**
 * Whether the panels show the shared legend: for two or more keys, unless a
 * bar or line tile turned its legend off. A pie always keeps it, as its
 * panels are too small for labels beside the slices.
 */
export const showsPanelLegend = (
  tile: RepeatTile,
  items: LegendItem[]
): boolean =>
  items.length > 1 && (tile.type === "pie" || tile.chart.legend !== "none")

/** Columns and rows for `count` panels in at most `columns` columns. */
export const panelGrid = (
  count: number,
  columns: number
): { columns: number; rows: number } => {
  const cols = Math.max(1, Math.min(columns, count))
  return { columns: cols, rows: Math.max(1, Math.ceil(count / cols)) }
}
